// Left column of the case brief: Income (ticket 072, D2/D9). One stable component for every
// client; conditional JSX on what the sections carry (rule 12). Order: IRPF card, income
// activities with no income entered, Rental and business detail, Autónomo, Scenarios (old brief).
import React from 'react'
import { formatEuro } from '../../../lib/briefUtils'
import { formatDateTime } from '../../../lib/briefFormat'
import { getAdvisorSessionStateLabel } from '../../HomeLink'
import { BASE_LABELS } from '../../TaxBaseExplainer'
import { RESULTS_RATE_YEAR_LINE } from '../../../lib/intake/intakeConfig'
import { FactGroup, SourceCardHeader } from './BriefParts'
import { GroupedIncomeTable, AdjustmentItemsTable, FilingComparisonNote } from './IncomeTables'
import { ScenariosCard } from './ScenariosCard'
import { EventLines } from './EventLines'

function IrpfCard({ sections }) {
  const estimate = sections.calculatorEstimate
  const isIntake = sections.source === 'intake'
  const title = isIntake ? 'IRPF estimate' : 'IRPF Calculator'

  if (!estimate) {
    if (isIntake) {
      return (
        <div className="brief-source-card">
          <SourceCardHeader title={title} />
          <div className="brief-source-card-body">
            {sections.intake.noEstimateReasons.length > 0
              ? sections.intake.noEstimateReasons.map((r) => <p className="brief-empty-note" key={r}>{r}</p>)
              : <p className="brief-empty-note">No IRPF estimate for this client.</p>}
          </div>
        </div>
      )
    }
    return (
      <div className={`brief-source-card${sections.products.calculator ? ' completed' : ''}`}>
        <SourceCardHeader title="IRPF Calculator" completed={sections.products.calculator} />
        <div className="brief-source-card-body">
          <p className="brief-empty-note">{sections.products.calculator ? 'No calculator estimate available.' : 'Not completed yet.'}</p>
        </div>
      </div>
    )
  }

  const hasRange = estimate.estimate_low != null && estimate.estimate_high != null
    && estimate.estimate_low !== estimate.estimate_high

  return (
    <div className="brief-source-card completed">
      {isIntake ? (
        <SourceCardHeader
          title={title}
          sub={sections.intake.rateYear ? RESULTS_RATE_YEAR_LINE(sections.intake.rateYear) : null}
        />
      ) : (
        <SourceCardHeader
          title={estimate.is_draft ? 'IRPF Calculator — Draft (unpublished)' : 'IRPF Calculator — Current'}
          sub={estimate.ran_at
            ? (estimate.is_draft
                ? `Last edited ${formatDateTime(estimate.ran_at)} · client hasn't saved this; figures may change`
                : `Run on ${formatDateTime(estimate.ran_at)}`)
            : null}
          completed
          badge={estimate.is_draft ? getAdvisorSessionStateLabel('draft') : undefined}
          badgeClass={estimate.is_draft ? 'brief-source-card-badge--draft' : ''}
          link={(!estimate.is_draft && estimate.public_token)
            ? { href: `/calculator/result/${estimate.public_token}`, label: 'View as client saw it ↗' }
            : null}
        />
      )}
      <div className="brief-source-card-body">
        <div className="brief-fact-row">
          <span className="brief-fact-label">Estimated IRPF{estimate.tax_year ? ` (${estimate.tax_year})` : ''}</span>
          <span className="brief-fact-value brief-calc-headline">
            {formatEuro(estimate.irpf_gross)}
            {hasRange && <span className="brief-calc-range"> ({formatEuro(estimate.estimate_low)}–{formatEuro(estimate.estimate_high)})</span>}
            {estimate.effective_rate != null && <span className="brief-calc-range"> ({estimate.effective_rate}% effective rate)</span>}
          </span>
        </div>
        {estimate.display_mode === 'adjustment_estimate' && (
          <div className="brief-adjustment-baseline-note">
            This is a conservative baseline — see potential reductions below.
          </div>
        )}

        <FilingComparisonNote comparison={sections.filingComparison} />

        {sections.incomeByOwner
          ? sections.incomeByOwner.map(ownerGroup => (
              <div key={ownerGroup.owner} className={ownerGroup.label ? `brief-income-owner-block brief-income-owner-block--${ownerGroup.owner}` : undefined}>
                {ownerGroup.label && <div className="brief-income-owner-heading">{ownerGroup.label}</div>}
                {ownerGroup.groups.map(g => (
                  <div key={g.base} className="brief-income-group">
                    <div className="brief-fact-group-title">{BASE_LABELS[g.base] || g.base}</div>
                    <GroupedIncomeTable items={g.items} />
                  </div>
                ))}
              </div>
            ))
          : <p className="brief-empty-note">No income items on this run.</p>}

        {sections.foreignTaxes.length > 0 && (
          <div className="brief-income-group">
            <div className="brief-fact-group-title">Foreign taxes paid</div>
            <div className="brief-findings-wrap">
              <table className="brief-findings-table brief-income-table">
                <tbody>
                  {sections.foreignTaxes.map((t) => (
                    <tr key={t.key} className="brief-findings-tr">
                      <td className="brief-findings-td">{t.label}</td>
                      <td className="brief-findings-td brief-findings-td-nowrap">{t.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {estimate.display_mode === 'adjustment_estimate' && (
          <AdjustmentItemsTable items={estimate.adjustment_items} />
        )}
      </div>
    </div>
  )
}

export function IncomeColumn({ sections, visibleSessions, viewedSessionId, onSelectSession, loadingSession }) {
  const isIntake = sections.source === 'intake'
  const showSnapshotIncome = !sections.calculatorEstimate && sections.incomeFacts.length > 0
  const hasActivities = showSnapshotIncome || sections.events.left.length > 0

  return (
    <div className="brief-col-primary">
      <IrpfCard sections={sections} />

      {hasActivities && (
        <div className="brief-source-card">
          <SourceCardHeader
            title={isIntake ? 'Income activities with no income entered' : 'Income activities'}
          />
          <div className="brief-source-card-body">
            {showSnapshotIncome && <FactGroup title="Income sources" facts={sections.incomeFacts} />}
            <EventLines events={sections.events.left} />
          </div>
        </div>
      )}

      {!isIntake && (
        <ScenariosCard
          sessions={visibleSessions}
          viewedSessionId={viewedSessionId}
          onSelect={onSelectSession}
          loading={loadingSession}
        />
      )}
    </div>
  )
}
